import React, { useState, useEffect } from "react";
import { ChevronDown, MapPin, Clock, Camera, X } from "lucide-react";

const NoviSadKrozVreme = () => {
  const [activeSection, setActiveSection] = useState("");
  const [isScrolled, setIsScrolled] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);

  // Gallery images
  const galleryImages = [
    {
      url: "/images/tvrdjava.webp",
      title: "Petrovaradinska Tvrđava",
    },
    {
      url: "/images/gradska-kuca.webp",
      title: "Gradska Kuća",
    },
    {
      url: "/images/dunavska-ulica.webp",
      title: "Dunavska Ulica",
    },
    {
      url: "/images/pogled-sa-tvrdjave.webp",
      title: "Pogled Sa Tvrđave",
    },
    {
      url: "/images/raskrsnica-kod-prom.webp",
      title: "Raskrsnica kod Promenada",
    },
    {
      url: "/images/jevrejska.webp",
      title: "Jevrejska Ulica",
    },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      const sections = document.querySelectorAll("section[id]");
      let current = "";

      sections.forEach((section) => {
        const sectionTop = section.offsetTop;
        //const sectionHeight = section.clientHeight;
        if (window.scrollY >= sectionTop - 200) {
          current = section.getAttribute("id");
        }
      });

      setActiveSection(current);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const sections = [
    { id: "hero", label: "Početna" },
    { id: "tvrdjava", label: "Tvrđava" },
    { id: "trg", label: "Trg Slobode" },
    { id: "dunavska", label: "Dunavska" },
    { id: "pogled", label: "Pogled" },
    { id: "promenada", label: "Promenada" },
    { id: "jevrejska", label: "Jevrejska" },
    { id: "park", label: "Park" },
    { id: "futoska", label: "Futoška" },
    { id: "galerija", label: "Galerija" },
  ];

  return (
    <div className="bg-stone-50 font-serif">
      {/* Navigation */}
      <nav
        className={`fixed top-0 w-full z-50 transition-all duration-500 ${
          isScrolled
            ? "bg-stone-900/95 backdrop-blur-sm shadow-lg py-3"
            : "bg-transparent py-6"
        }`}
      >
        <div className="container mx-auto px-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div
                className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all ${
                  isScrolled
                    ? "border-amber-500 text-amber-500"
                    : "border-white text-white"
                }`}
              >
                <MapPin className="w-5 h-5" />
              </div>
              <h1
                className={`text-2xl font-bold tracking-wide transition-colors ${
                  isScrolled ? "text-white" : "text-white drop-shadow-lg"
                }`}
              >
                Novi Sad Kroz Vreme
              </h1>
            </div>
            <div className="hidden md:flex space-x-1">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`px-4 py-2 text-sm transition-all rounded-full ${
                    activeSection === section.id
                      ? "bg-amber-500 text-stone-900 font-semibold"
                      : isScrolled
                      ? "text-stone-200 hover:text-amber-400"
                      : "text-white hover:text-amber-300"
                  }`}
                >
                  {section.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section
        id="hero"
        className="relative h-screen flex items-center justify-center overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-stone-900 via-emerald-900 to-stone-800" />
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "url(https://placehold.co/1920x1080/1A4D2E/FFFFFF?text=Novi+Sad+Panorama)",
            // "url(/images/pogled-sa-tvrdjave.webp)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />

        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto animate-fade-in">
          <div className="inline-block mb-6 px-6 py-2 bg-amber-500/20 backdrop-blur-sm rounded-full border border-amber-500/30">
            <span className="text-amber-300 text-sm tracking-widest uppercase font-sans">
              Istorija i Sadašnjost
            </span>
          </div>
          <h1 className="text-6xl md:text-8xl font-bold text-white mb-6 leading-tight tracking-tight">
            Novi Sad
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">
              Kroz Vreme
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-stone-300 mb-12 leading-relaxed max-w-3xl mx-auto font-sans">
            Otkrijte priču o gradu na Dunavu — od srednjovekovne tvrđave do
            savremenog kulturnog centra
          </p>
          <button
            onClick={() => scrollToSection("tvrdjava")}
            className="group bg-amber-500 hover:bg-amber-600 text-stone-900 px-8 py-4 rounded-full font-bold text-lg transition-all transform hover:scale-105 shadow-xl hover:shadow-2xl flex items-center mx-auto space-x-2"
          >
            <span>Počnite Putovanje</span>
            <ChevronDown className="w-5 h-5 group-hover:translate-y-1 transition-transform" />
          </button>
        </div>

        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-white/60" />
        </div>
      </section>

      {/* Petrovaradinska Tvrdjava */}
      <section id="tvrdjava" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1 space-y-6 animate-on-scroll">
              <div className="inline-flex items-center space-x-2 text-amber-600 mb-4">
                <Clock className="w-5 h-5" />
                <span className="text-sm font-sans tracking-wider uppercase">
                  1692 - Danas
                </span>
              </div>
              <h2 className="text-5xl font-bold text-stone-900 leading-tight">
                Petrovaradinska
                <br />
                Tvrđava
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-amber-500 to-amber-600" />

              <div className="space-y-4 text-stone-700 leading-relaxed">
                <p className="text-lg font-sans">
                  <span className="font-bold text-stone-900">Istorija:</span>{" "}
                  Petrovaradinska tvrđava, poznata kao "Gibraltar na Dunavu",
                  izgrađena je između 1692. i 1780. godine od strane Habzburške
                  monarhije. Ova monumentalna fortifikacija igrala je ključnu
                  ulogu u odbrani od Osmanskog carstva.
                </p>
                <p className="text-lg font-sans">
                  Tokom Bitke kod Petrovaradina 1716. godine, princ Eugen
                  Savojski postigao je odlučujuću pobedu koja je označila
                  preokret u ratovima sa Turcima.
                </p>
                <p className="text-lg font-sans">
                  <span className="font-bold text-stone-900">Danas:</span>{" "}
                  Tvrđava je dom Muzeja grada Novog Sada, Planetarijuma, i
                  poznatog EXIT festivala koji svake godine privlači stotine
                  hiljada posetilaca iz celog sveta. Njeni kilometarski podzemni
                  hodnici i bastioni nude jedinstveni pogled na gradsku
                  istoriju.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4 pt-6">
                <div className="text-center p-4 bg-stone-50 rounded-lg">
                  <div className="text-3xl font-bold text-amber-600">88</div>
                  <div className="text-sm text-stone-600 font-sans">
                    godina gradnje
                  </div>
                </div>
                <div className="text-center p-4 bg-stone-50 rounded-lg">
                  <div className="text-3xl font-bold text-amber-600">112</div>
                  <div className="text-sm text-stone-600 font-sans">
                    hektara površine
                  </div>
                </div>
                <div className="text-center p-4 bg-stone-50 rounded-lg">
                  <div className="text-3xl font-bold text-amber-600">16km</div>
                  <div className="text-sm text-stone-600 font-sans">tunela</div>
                </div>
              </div>
            </div>

            <div className="order-1 md:order-2">
              <div className="relative group">
                <div className="absolute -inset-4 bg-gradient-to-r from-amber-500 to-emerald-500 rounded-2xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity" />
                <img
                  src="/images/tvrdjava.webp"
                  alt="Petrovaradinska Tvrđava"
                  className="relative rounded-2xl shadow-2xl w-full transform group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trg Slobode */}
      <section id="trg" className="py-24 bg-stone-100">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="relative group">
                <div className="absolute -inset-4 bg-gradient-to-r from-emerald-500 to-amber-500 rounded-2xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity" />
                <img
                  src="/images/gradska-kuca.webp"
                  alt="Trg Slobode"
                  className="relative rounded-2xl shadow-2xl w-full transform group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>

            <div className="space-y-6">
              <div className="inline-flex items-center space-x-2 text-emerald-600 mb-4">
                <MapPin className="w-5 h-5" />
                <span className="text-sm font-sans tracking-wider uppercase">
                  Centar Grada
                </span>
              </div>
              <h2 className="text-5xl font-bold text-stone-900 leading-tight">
                Trg Slobode
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-emerald-500 to-emerald-600" />

              <div className="space-y-4 text-stone-700 leading-relaxed">
                <p className="text-lg font-sans">
                  <span className="font-bold text-stone-900">Istorija:</span>{" "}
                  Trg Slobode je glavni gradski trg, formiran u 18. veku kao
                  trgovački centar. Kroz vekove je nosio različita imena - od
                  Franz Joseph Platz u doba Austro-Ugarske, do današnjeg naziva
                  koji slavi oslobođenje.
                </p>
                <p className="text-lg font-sans">
                  Dominantnu ulogu na trgu ima katolička crkva Imena Marijinog
                  iz 1895. godine, remek-delo neogotičkog stila sa tornjem
                  visokim 72 metra.
                </p>
                <p className="text-lg font-sans">
                  <span className="font-bold text-stone-900">Danas:</span> Trg
                  je živo srce grada, okružen kafićima, restoranima i kultnim
                  zgradama. Ovde se održavaju koncerti, priredbe, manifestacije.
                  Statua Svetozara Miletića, veliko srpskog političara, ponosno
                  stoji u centru trga od 1939. godine.
                </p>
              </div>

              <div className="bg-emerald-50 p-6 rounded-xl border-l-4 border-emerald-500">
                <p className="text-stone-700 italic font-sans">
                  "Trg Slobode je mesto gde se susreću istorija i savremeni
                  život, gde svaka kocka pamti korake generacija."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dunavska Ulica */}
      <section id="dunavska" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1 space-y-6">
              <div className="inline-flex items-center space-x-2 text-amber-600 mb-4">
                <Clock className="w-5 h-5" />
                <span className="text-sm font-sans tracking-wider uppercase">
                  18. vek - Danas
                </span>
              </div>
              <h2 className="text-5xl font-bold text-stone-900 leading-tight">
                Dunavska Ulica
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-amber-500 to-amber-600" />

              <div className="space-y-4 text-stone-700 leading-relaxed">
                <p className="text-lg font-sans">
                  <span className="font-bold text-stone-900">Istorija:</span>{" "}
                  Dunavska ulica jedna je od najstarijih ulica u Novom Sadu,
                  formirana uporedo sa razvojem grada u 18. veku. Bila je glavna
                  trgovačka arterija koja je povezivala luku sa centrom grada.
                </p>
                <p className="text-lg font-sans">
                  U prošlosti, ovde su se nalazile skladišta, zanatlijske radnje
                  i trgovine. Arhitektura odražava bogatstvo građanske klase iz
                  perioda Austro-Ugarske monarhije.
                </p>
                <p className="text-lg font-sans">
                  <span className="font-bold text-stone-900">Danas:</span>{" "}
                  Dunavska je popularna šetališna zona sa očuvanim fasadama,
                  modernim kafićima, buticima i galerijama. Ulica odiše
                  autentičnim ambijentom starog Novog Sada, privlačeći kako
                  lokalno stanovništvo tako i turiste.
                </p>
              </div>

              <div className="flex flex-wrap gap-3 pt-4">
                <span className="px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-sans">
                  Pešačka zona
                </span>
                <span className="px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-sans">
                  Istorijska arhitektura
                </span>
                <span className="px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-sans">
                  Noćni život
                </span>
              </div>
            </div>

            <div className="order-1 md:order-2">
              <div className="relative group">
                <div className="absolute -inset-4 bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity" />
                <img
                  src="/images/dunavska-ulica.webp"
                  alt="Dunavska Ulica"
                  className="relative rounded-2xl shadow-2xl w-full transform group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pogled sa Tvrdjave */}
      <section
        id="pogled"
        className="py-24 bg-gradient-to-br from-stone-900 to-emerald-900 text-white"
      >
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <div className="inline-flex items-center space-x-2 text-amber-400 mb-4">
              <Camera className="w-5 h-5" />
              <span className="text-sm font-sans tracking-wider uppercase">
                Panoramski Pogled
              </span>
            </div>
            <h2 className="text-5xl font-bold mb-6 leading-tight">
              Šta Možete Videti
              <br />
              Sa Tvrđave
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-amber-500 to-amber-600 mx-auto mb-8" />
            <p className="text-xl text-stone-300 leading-relaxed font-sans">
              Sa bedema Petrovaradinske tvrđave otvara se spektakularan pogled
              na grad, reku i okolinu
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20 hover:bg-white/15 transition-all transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mb-6">
                <MapPin className="w-8 h-8 text-amber-400" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Dunav</h3>
              <p className="text-stone-300 leading-relaxed font-sans">
                Veličanstvena reka se prostire ispred vas, sa mostovima koji
                povezuju dva dela grada. Pogled na Dunav, posebno tokom zalaska
                sunca, nezaboravan je doživljaj.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20 hover:bg-white/15 transition-all transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mb-6">
                <MapPin className="w-8 h-8 text-emerald-400" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Centar Grada</h3>
              <p className="text-stone-300 leading-relaxed font-sans">
                Panorama centra Novog Sada sa crkvenim tornjevima, kupolama i
                krovovima. Može se uočiti Crkva imena Marijinog, Crkva Svetog
                Đorđa i druga znamenita zdanja.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20 hover:bg-white/15 transition-all transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mb-6">
                <MapPin className="w-8 h-8 text-blue-400" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Fruška Gora</h3>
              <p className="text-stone-300 leading-relaxed font-sans">
                U pozadini se ističe planina Fruška Gora sa svojim šumovitim
                obroncima, koja čuva brojne pravoslavne manastire i prirodne
                lepote.
              </p>
            </div>
          </div>

          {/* <div className="mt-16 relative rounded-3xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-t from-stone-900/90 to-transparent z-10" />
            <img
              src="/images/pogled-sa-tvrdjave.webp"
              alt="Pogled sa Tvrđave"
              className="w-full h-96 object-cover"
            />
            <div className="absolute bottom-8 left-8 z-20 text-white">
              <p className="text-3xl font-bold">360° panorama grada</p>
              <p className="text-lg text-stone-300 font-sans">
                Najbolji pogled u Vojvodini
              </p>
            </div>
          </div> */}
        </div>
      </section>

      {/* Promenada */}
      <section id="promenada" className="py-24 bg-stone-100">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="relative group">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-500 to-emerald-500 rounded-2xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity" />
                <img
                  src="/images/raskrsnica-kod-prom.webp"
                  alt="Promenada"
                  className="relative rounded-2xl shadow-2xl w-full transform group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>

            <div className="space-y-6">
              <div className="inline-flex items-center space-x-2 text-blue-600 mb-4">
                <MapPin className="w-5 h-5" />
                <span className="text-sm font-sans tracking-wider uppercase">
                  Obala Dunava
                </span>
              </div>
              <h2 className="text-5xl font-bold text-stone-900 leading-tight">
                Raskrsnice Kod
                <br />
                Promenade
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-blue-500 to-blue-600" />

              <div className="space-y-4 text-stone-700 leading-relaxed">
                <p className="text-lg font-sans">
                  <span className="font-bold text-stone-900">Istorija:</span>{" "}
                  Kej uz Dunav bio je važna trgovačka zona gde su pristajali
                  brodovi sa robom. Raskrsnice koje vode od obale ka centru
                  grada bile su mesto intenzivnog saobraćaja i trgovine.
                </p>
                <p className="text-lg font-sans">
                  Tokom austrougarske vladavine, Kej je uređen kao moderna obala
                  sa skladištima i pristanišnim objektima. Raskrsnice su
                  planirane da omoguće efikasan transport robe ka trgovačkom
                  centru.
                </p>
                <p className="text-lg font-sans">
                  <span className="font-bold text-stone-900">Danas:</span> Kej
                  Žrtava Racije je omiljeno šetalište Novosađana. Raskrsnice
                  vode ka Dunavskoj ulici i drugim znamenitim lokacijama.
                  Promenada je mesto za rekreaciju, biciklizam, jahte, splav
                  restorane i letnje festivale.
                </p>
              </div>

              <div className="bg-blue-50 p-6 rounded-xl">
                <h4 className="font-bold text-stone-900 mb-3 font-sans">
                  Popularne raskrsnice:
                </h4>
                <ul className="space-y-2 text-stone-700 font-sans">
                  <li className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full" />
                    <span>Ulica Grčkoškolska</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full" />
                    <span>Ulica Laze Telečkog</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full" />
                    <span>Ulica Zmaj Jovina</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Jevrejska i Pasiceva */}
      <section id="jevrejska" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1 space-y-6">
              <div className="inline-flex items-center space-x-2 text-purple-600 mb-4">
                <MapPin className="w-5 h-5" />
                <span className="text-sm font-sans tracking-wider uppercase">
                  Istorijska Raskrsnica
                </span>
              </div>
              <h2 className="text-5xl font-bold text-stone-900 leading-tight">
                Ugao Jevrejske i<br />
                Pašićeva
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-purple-500 to-purple-600" />

              <div className="space-y-4 text-stone-700 leading-relaxed">
                <p className="text-lg font-sans">
                  <span className="font-bold text-stone-900">Istorija:</span>{" "}
                  Jevrejska ulica dobila je ime po značajnoj jevrejskoj
                  zajednici koja je vekovima živela u Novom Sadu. U blizini se
                  nalazi Jevrejska sinagoga iz 1909. godine, veličanstvena
                  građevina u secesijskom stilu.
                </p>
                <p className="text-lg font-sans">
                  Ugao sa ulicom Pašićevom (nekadašnja Kralja Aleksandra) bio je
                  važna tačka gradskog razvoja, sa elegantnim zgradama iz
                  perioda belle époque. Ovde su se nalazile prodavnice,
                  kancelarije i stanovi imućnijih građana.
                </p>
                <p className="text-lg font-sans">
                  <span className="font-bold text-stone-900">Danas:</span> Ova
                  raskrsnica spaja istorijsko i moderno. Sinagoga je i dalje
                  aktivna i služi kao kulturni centar. U okolnim ulicama nalaze
                  se galerije, kafići, butici i kancelarije, čineći ovu lokaciju
                  živopisnim delom grada.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-6">
                <div className="bg-purple-50 p-6 rounded-xl">
                  <div className="text-4xl mb-2">🕍</div>
                  <p className="font-bold text-stone-900 font-sans">Sinagoga</p>
                  <p className="text-sm text-stone-600 font-sans">
                    Izgradnja 1909.
                  </p>
                </div>
                <div className="bg-purple-50 p-6 rounded-xl">
                  <div className="text-4xl mb-2">🏛️</div>
                  <p className="font-bold text-stone-900 font-sans">Secesija</p>
                  <p className="text-sm text-stone-600 font-sans">
                    Arhitektonski stil
                  </p>
                </div>
              </div>
            </div>

            <div className="order-1 md:order-2">
              <div className="relative group">
                <div className="absolute -inset-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity" />
                <img
                  src="/images/jevrejska.webp"
                  alt="Jevrejska ulica"
                  className="relative rounded-2xl shadow-2xl w-full transform group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dunavski Park */}
      <section
        id="park"
        className="py-24 bg-gradient-to-br from-emerald-50 to-green-50"
      >
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="relative group">
                <div className="absolute -inset-4 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity" />
                <img
                  src="/images/gradska-kuca.webp"
                  alt="Dunavski Park"
                  className="relative rounded-2xl shadow-2xl w-full transform group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>

            <div className="space-y-6">
              <div className="inline-flex items-center space-x-2 text-green-600 mb-4">
                <MapPin className="w-5 h-5" />
                <span className="text-sm font-sans tracking-wider uppercase">
                  Zelena Oaza
                </span>
              </div>
              <h2 className="text-5xl font-bold text-stone-900 leading-tight">
                Dunavski Park
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-green-500 to-emerald-600" />

              <div className="space-y-4 text-stone-700 leading-relaxed">
                <p className="text-lg font-sans">
                  <span className="font-bold text-stone-900">Istorija:</span>{" "}
                  Dunavski park osnovan je 1895. godine kao prvi javni park u
                  Novom Sadu. Projektovan je u duhu engleskih parkova sa šetnim
                  stazama, jezercem i bogatom vegetacijom.
                </p>
                <p className="text-lg font-sans">
                  Park je kroz decenije bio omiljeno mesto za odmor i rekreaciju
                  građana. Tokom Drugog svetskog rata pretrpeo je oštećenja, ali
                  je obnovljen i proširen u posleratnom periodu.
                </p>
                <p className="text-lg font-sans">
                  <span className="font-bold text-stone-900">Danas:</span>{" "}
                  Dunavski park je najlepša zelena površina u centru grada. Ovde
                  se nalaze skulpture, fontane, dečje igralište, i omiljena
                  bista Jožefa Atile. Park je domaćin kulturnih događaja,
                  koncerata i izložbi pod vedrim nebom.
                </p>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border-l-4 border-green-500">
                <h4 className="font-bold text-stone-900 mb-3 font-sans">
                  Zanimljivosti:
                </h4>
                <ul className="space-y-2 text-stone-700 font-sans">
                  <li className="flex items-start space-x-3">
                    <span className="text-green-500 font-bold">•</span>
                    <span>Preko 300 stabala različitih vrsta</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-green-500 font-bold">•</span>
                    <span>Istorijsko jezerce sa mostićima</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-green-500 font-bold">•</span>
                    <span>Spomenik posvećen stradalim Jevrejima</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Futoska Ulica */}
      <section id="futoska" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1 space-y-6">
              <div className="inline-flex items-center space-x-2 text-orange-600 mb-4">
                <MapPin className="w-5 h-5" />
                <span className="text-sm font-sans tracking-wider uppercase">
                  Glavna Arterija
                </span>
              </div>
              <h2 className="text-5xl font-bold text-stone-900 leading-tight">
                Futoška Ulica
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-orange-500 to-orange-600" />

              <div className="space-y-4 text-stone-700 leading-relaxed">
                <p className="text-lg font-sans">
                  <span className="font-bold text-stone-900">Istorija:</span>{" "}
                  Futoška ulica dobila je ime po susednom naselju Futog. Ova
                  saobraćajnica postojala je još od 18. veka kao put koji je
                  povezivao Novi Sad sa okolnim naseljima i Bačkom.
                </p>
                <p className="text-lg font-sans">
                  U austrougarsko vreme, ulica je postala važan trgovački
                  pravac. Duž nje su niknule radnje, zanatlijske radionice,
                  gostionice i kuće bogatijih trgovaca.
                </p>
                <p className="text-lg font-sans">
                  <span className="font-bold text-stone-900">Danas:</span>{" "}
                  Futoška je jedna od najfrekventnijih ulica u gradu, sa širokim
                  spektrom sadržaja - od trgovina, restorana, kafića, do
                  poslovnih zgrada. Prolazi kroz više gradskih četvrti i
                  povezuje centar sa periferijom.
                </p>
              </div>

              <div className="bg-orange-50 p-6 rounded-xl">
                <h4 className="font-bold text-stone-900 mb-4 font-sans">
                  Karakteristike ulice:
                </h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between pb-2 border-b border-orange-200">
                    <span className="text-stone-700 font-sans">Dužina</span>
                    <span className="font-bold text-orange-600">~7 km</span>
                  </div>
                  <div className="flex items-center justify-between pb-2 border-b border-orange-200">
                    <span className="text-stone-700 font-sans">Traka</span>
                    <span className="font-bold text-orange-600">
                      Četvorotračna
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-stone-700 font-sans">
                      Javni prevoz
                    </span>
                    <span className="font-bold text-orange-600">
                      10+ linija
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="order-1 md:order-2">
              <div className="relative group">
                <div className="absolute -inset-4 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity" />
                <img
                  src="/images/dunavska-ulica.webp"
                  alt="Futoška ulica"
                  className="relative rounded-2xl shadow-2xl w-full transform group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="galerija" className="py-24 bg-stone-900">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 text-amber-400 mb-4">
              <Camera className="w-5 h-5" />
              <span className="text-sm font-sans tracking-wider uppercase">
                Vizuelno Putovanje
              </span>
            </div>
            <h2 className="text-5xl font-bold text-white mb-6 leading-tight">
              Galerija Novog Sada
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-amber-500 to-amber-600 mx-auto mb-8" />
            <p className="text-xl text-stone-300 max-w-3xl mx-auto leading-relaxed font-sans">
              Istražite lepotu grada kroz objektiv - od istorijskih građevina do
              savremenih veduta
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
            {galleryImages.map((img, index) => (
              <div
                key={index}
                onClick={() => {
                  setSelectedImage(index);
                  setGalleryOpen(true);
                }}
                className="relative group cursor-pointer overflow-hidden rounded-xl aspect-square"
              >
                <img
                  src={img.url}
                  alt={img.title}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                  <p className="text-white font-bold font-sans">{img.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Modal */}
      {galleryOpen && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4">
          <button
            onClick={() => setGalleryOpen(false)}
            className="absolute top-6 right-6 text-white hover:text-amber-400 transition-colors"
          >
            <X className="w-8 h-8" />
          </button>

          <button
            onClick={() =>
              setSelectedImage(
                (selectedImage - 1 + galleryImages.length) %
                  galleryImages.length
              )
            }
            className="absolute left-6 text-white hover:text-amber-400 transition-colors"
          >
            <ChevronDown className="w-12 h-12 rotate-90" />
          </button>

          <button
            onClick={() =>
              setSelectedImage((selectedImage + 1) % galleryImages.length)
            }
            className="absolute right-6 text-white hover:text-amber-400 transition-colors"
          >
            <ChevronDown className="w-12 h-12 -rotate-90" />
          </button>

          <div className="max-w-5xl w-full">
            <img
              src={galleryImages[selectedImage].url}
              alt={galleryImages[selectedImage].title}
              className="w-full rounded-2xl shadow-2xl"
            />
            <p className="text-white text-center mt-6 text-2xl font-bold">
              {galleryImages[selectedImage].title}
            </p>
            <p className="text-stone-400 text-center mt-2 font-sans">
              {selectedImage + 1} / {galleryImages.length}
            </p>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-stone-900 text-white py-16">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 rounded-full border-2 border-amber-500 flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-amber-500" />
                </div>
                <h3 className="text-2xl font-bold">
                  Novi Sad
                  <br />
                  Kroz Vreme
                </h3>
              </div>
              <p className="text-stone-400 leading-relaxed font-sans">
                Otkrijte priču o gradu na Dunavu kroz vekove istorije i
                savremenog razvoja.
              </p>
            </div>

            <div>
              <h4 className="text-xl font-bold mb-6">Brzi Linkovi</h4>
              <ul className="space-y-3">
                {sections.slice(1, -1).map((section) => (
                  <li key={section.id}>
                    <button
                      onClick={() => scrollToSection(section.id)}
                      className="text-stone-400 hover:text-amber-400 transition-colors font-sans"
                    >
                      {section.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-xl font-bold mb-6">Informacije</h4>
              <div className="space-y-4">
                <p className="text-stone-400 font-sans">
                  <span className="text-white font-bold">Email:</span>
                  <br />
                  info@novisadkrozvreme.rs
                </p>
                <p className="text-stone-400 font-sans">
                  <span className="text-white font-bold">Adresa:</span>
                  <br />
                  Novi Sad, Srbija
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-stone-800 pt-8 text-center">
            <p className="text-stone-500 font-sans">
              © 2024 Novi Sad Kroz Vreme. Sva prava zadržana.
            </p>
          </div>
        </div>
      </footer>

      <style jsx>{`
        @import url("https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=Inter:wght@300;400;600;700&display=swap");

        .font-serif {
          font-family: "Playfair Display", serif;
        }

        .font-sans {
          font-family: "Inter", sans-serif;
        }

        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 1s ease-out;
        }

        .animate-on-scroll {
          animation: fade-in 0.8s ease-out;
        }

        html {
          scroll-behavior: smooth;
        }
      `}</style>
    </div>
  );
};

export default NoviSadKrozVreme;
